/**
 * useStarAward.ts
 * Provides a `grantStar` function that awards stars to a student,
 * triggers XP logic, and queues level-up animations.
 */

import { useCallback } from 'react';
import { awardStars } from '@db/studentService';
import { useAppStore } from '@store/appStore';
import { useClassStore } from '@store/classStore';
import type { StarAwardEvent } from '@types-local/class.types';

interface UseStarAwardReturn {
  grantStars: (studentId: string, studentName: string, stars?: number) => Promise<void>;
}

export function useStarAward(): UseStarAwardReturn {
  const activeClassId = useAppStore((s) => s.activeClassId);
  const teacherUid = useAppStore((s) => s.teacherUid);
  const addLevelUp = useClassStore((s) => s.addLevelUp);
  const addStarAward = useClassStore((s) => s.addStarAward);

  const grantStars = useCallback(
    async (studentId: string, studentName: string, stars = 1) => {
      if (!activeClassId) {
        console.warn('[GuroHub] No active class selected');
        return;
      }

      try {
        const { newLevel, leveledUp } = await awardStars(activeClassId, studentId, stars);

        // Record the star award event for overlay toasts
        const event: StarAwardEvent = {
          studentId,
          studentName,
          stars,
          grantedBy: teacherUid ?? 'teacher',
          timestamp: Date.now(),
        };
        addStarAward(event);

        // Queue level-up animation if applicable
        if (leveledUp) {
          addLevelUp(studentId, newLevel);
        }
      } catch (err) {
        console.error('[GuroHub] grantStars failed:', err);
      }
    },
    [activeClassId, teacherUid, addLevelUp, addStarAward],
  );

  return { grantStars };
}
